import jsPDF from 'jspdf';

import { blobToBase64 } from '@utils/blobToBase64';

export const usePdf = () => {
  // The dimensions are in millimeters.
  const LETTER_PAPER_DIMENSIONS = {
    width: 210,
    height: 297,
  };

  const LETTER_PAPER_RATIO = LETTER_PAPER_DIMENSIONS.width / LETTER_PAPER_DIMENSIONS.height;

  interface ImageDimension {
    width: number;
    height: number;
  }

  // Calculates the best possible position of an image on the LETTER paper format,
  // so that the maximal area of LETTER is used and the image ratio is preserved.
  const imageDimensionsOnLetter = (dimensions: ImageDimension) => {
    const isLandscapeImage = dimensions.width >= dimensions.height;

    // If the image is in landscape, the full width of LETTER is used.
    if (isLandscapeImage) {
      const buffer = {
        height: LETTER_PAPER_DIMENSIONS.width,
        width: LETTER_PAPER_DIMENSIONS.height,
      };

      return {
        width: buffer.width,
        height: buffer.width / (dimensions.width / dimensions.height),
      };
    }

    // If the image is in portrait and the full height of LETTER would skew
    // the image ratio, we scale the image dimensions.
    const imageRatio = dimensions.width / dimensions.height;
    if (imageRatio > LETTER_PAPER_RATIO) {
      const imageScaleFactor = (LETTER_PAPER_RATIO * dimensions.height) / dimensions.width;

      const scaledImageHeight = LETTER_PAPER_DIMENSIONS.height * imageScaleFactor;

      return {
        height: scaledImageHeight,
        width: scaledImageHeight * imageRatio,
      };
    }

    // The full height of LETTER can be used without skewing the image ratio.
    return {
      width: LETTER_PAPER_DIMENSIONS.height / (dimensions.height / dimensions.width),
      height: LETTER_PAPER_DIMENSIONS.height,
    };
  };

  const getImageFromBase64 = (url: string): Promise<HTMLImageElement> => {
    return new Promise((resolved) => {
      const img = new Image();
      img.src = url;
      img.onload = function () {
        resolved(img);
      };
    });
  };

  const getCorrectImagesList = async (images: string[]) => {
    const getImage = await images.map(async (image) => {
      if (image.split(',')[1]) {
        const data = await getImageFromBase64(image);
        return data;
      } else {
        const data = await fetch(image, { cache: 'no-cache' });
        const blob = await data.blob();
        const base64 = await blobToBase64(blob);
        const correctImage = await getImageFromBase64(base64);
        return correctImage;
      }
    });

    const data = await Promise.all(getImage);

    return data;
  };

  const generatePdfFromImages = (images: HTMLImageElement[], imageType: string) => {
    // Default export is LETTER paper, portrait, using millimeters for units.
    const doc = new jsPDF();

    // We let the images add all pages,
    // therefore the first default page can be removed.
    doc.deletePage(1);

    images.forEach((image) => {
      const imageDimensions = imageDimensionsOnLetter({
        width: image.width,
        height: image.height,
      });
      doc.addPage('a4', image.width < image.height ? 'p' : 'l');
      const landscape = imageDimensions.width >= imageDimensions.height;

      const buffer = {
        width: landscape ? LETTER_PAPER_DIMENSIONS.height : LETTER_PAPER_DIMENSIONS.width,
        height: landscape ? LETTER_PAPER_DIMENSIONS.width : LETTER_PAPER_DIMENSIONS.height,
      };

      const x = landscape ? 0 : (buffer.width - imageDimensions.width) / 2;
      const y = landscape ? (buffer.height - imageDimensions.height) / 2 : 0;

      doc.addImage(image, imageType, x, y, imageDimensions.width, imageDimensions.height);

      // getMeta(image, (width: number, height: number) => {
      //   const imageDimensions = imageDimensionsOnLETTER({
      //     width: width,
      //     height: height,
      //   });

      // });
    });

    // Creates a PDF and opens it in a new browser tab.
    const pdf = doc.output('blob');
    // doc.output('dataurlnewwindow');
    return pdf;
    // window.open(pdfURL as any, '_blank');
  };

  return { generatePdfFromImages, getImageFromBase64, getCorrectImagesList };
};
